import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { ChainPerformance as Perf, ChainStep, performanceApi } from '../../services/performanceApi';
import { ReliabilityDialog } from '../reliability/ReliabilityDialog';
import { SectionTitle, StatCard, fmtBucket, fmtCredits, fmtMs, fmtNum, fmtPct, fmtS } from './common';
import { QuickLinks } from './QuickLinks';
import { usePageTitle } from '../../utils/pageTitle';
import { usePersistentState } from '../../utils/usePersistentState';

const WINDOWS = [1, 7, 30, 90];
const PRESET_HELP: Record<string, string> = {
  relaxed: 'Slowest 1% × 1.5. Rarely cuts a run off.',
  balanced: 'Slowest 5% × 1.5. The default.',
  strict: 'Slowest 5% × 1.2. Gives up sooner.',
};
const MODE_LABELS: Record<string, string> = {
  off: 'No time limit set',
  fixed: 'Fixed limit',
  auto: 'Learned from history',
};
const STEP_BACKUP_WARN = 0.1;

const toS = (ms: number | null) => (ms === null ? null : Math.round(ms / 100) / 10);

interface Props {
  chainId: string;
  customerId?: string | null;
}

export const ChainPerformance: React.FC<Props> = ({ chainId, customerId }) => {
  const { getIdToken } = useAuth();
  const navigate = useNavigate();
  const api = useMemo(() => performanceApi(getIdToken), [getIdToken]);
  const [days, setDays] = usePersistentState('chainPerformance.days', 30, WINDOWS);
  const [data, setData] = useState<Perf | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reliabilityOpen, setReliabilityOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .chain(chainId, days, customerId)
      .then(d => !cancelled && setData(d))
      .catch(e => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load performance'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, chainId, days, customerId, reloadKey]);

  const unit = data?.window.unit ?? 'day';

  const rows = useMemo(
    () =>
      (data?.series ?? []).map(b => {
        const failed = b.runs - b.ok;
        return {
          label: fmtBucket(b.bucket, unit),
          p50: toS(b.p50_ms),
          p95: toS(b.p95_ms),
          p99: toS(b.p99_ms),
          limit: b.learned_limit_s,
          timedOut: b.timed_out || null,
          clean: Math.max(0, b.ok - b.fallback),
          backup: b.fallback,
          timeout: b.timed_out,
          failed: Math.max(0, failed - b.timed_out),
          per1k: b.credits_per_1k_runs,
        };
      }),
    [data, unit]
  );

  const slowestStep = useMemo(() => {
    const steps = (data?.steps ?? []).filter(s => s.p50_ms !== null);
    return steps.length ? steps.reduce((a, b) => ((b.p50_ms ?? 0) > (a.p50_ms ?? 0) ? b : a)).node_id : null;
  }, [data]);

  usePageTitle(data ? `${data.chain.name} · Chain performance` : null);

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const s = data.summary;
  const tl = data.time_limit;
  // The dialog edits the signed-in account's own settings.
  const canEditReliability = !customerId;

  const openFunction = (step: ChainStep) => {
    if (!step.function_id) return;
    const params = new URLSearchParams({ tab: 'performance' });
    if (customerId) params.append('customer_id', customerId);
    navigate(`/dashboard/function-runs/${step.function_id}?${params.toString()}`);
  };

  return (
    <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 120ms' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {data.chain.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Finished runs, {unit === 'hour' ? 'per hour' : 'per day'}. Reruns from a step are left out. Times are for completed runs.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
            {WINDOWS.map(d => (
              <ToggleButton key={d} value={d}>
                {d === 1 ? '24h' : `${d}d`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <QuickLinks kind="chain" id={data.chain.id} name={data.chain.name} editable={!customerId} onReliabilitySaved={() => setReloadKey(k => k + 1)} />
        </Box>
      </Box>

      {!s || s.runs === 0 ? (
        <Alert severity="info">No finished runs in this window.</Alert>
      ) : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2.4}>
              <StatCard label="Runs" value={fmtNum(s.runs)} caption={`${fmtPct(s.success_rate)} completed`} />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <StatCard
                label="Typical / slow / slowest"
                value={fmtMs(s.p50_ms)}
                caption={`p95 ${fmtMs(s.p95_ms)} · p99 ${fmtMs(s.p99_ms)}`}
                tooltip="p50 is the typical run. p95 and p99: only 5% and 1% of completed runs took longer."
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <StatCard
                label="A step used a backup"
                value={fmtPct(s.backup_share)}
                caption={`${fmtNum(s.fallback)} runs`}
                tooltip="Runs where at least one step's first model failed or was too slow and a backup model answered. See the steps table for which step."
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <StatCard
                label="Timed out"
                value={fmtPct(s.timeout_share)}
                caption={`${fmtNum(s.timed_out)} runs`}
                tooltip="Runs stopped by the chain's time limit."
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <StatCard
                label="Credits per 1,000 runs"
                value={fmtCredits(s.credits_per_1k_runs)}
                caption={`${fmtCredits(s.credits)} total · ${fmtNum(Math.round(s.tokens / s.runs))} tokens/run`}
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <SectionTitle
              title="Steps"
              help="Each step's numbers across the runs in this window. Steps can run in parallel, so shares of a typical run may add up to more than 100%."
            />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Step</TableCell>
                  <TableCell align="right">Runs</TableCell>
                  <TableCell align="right">Success</TableCell>
                  <TableCell align="right">Typical</TableCell>
                  <TableCell align="right">p95</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <Tooltip title="This step's typical time as a share of the chain's typical run time.">
                      <span style={{ cursor: 'help' }}>Share of a typical run</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">Used a backup</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">Credits / 1k runs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.steps.map(step => (
                  <TableRow key={step.node_id}>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        {step.function_id ? (
                          <Tooltip title="Open this function's Performance tab">
                            <Link component="button" variant="body2" onClick={() => openFunction(step)} underline="hover">
                              {step.label}
                            </Link>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2">{step.label}</Typography>
                        )}
                        {step.kind === 'tool' && <Chip size="small" variant="outlined" label="tool" />}
                        {step.kind === 'removed' && (
                          <Tooltip title="This step is no longer in the chain; its numbers come from older runs.">
                            <Chip size="small" variant="outlined" label="removed" />
                          </Tooltip>
                        )}
                        {step.node_id === slowestStep && data.steps.length > 1 && <Chip size="small" color="info" label="slowest" />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{fmtNum(step.runs)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={step.failed ? 'error.main' : undefined}>
                        {fmtPct(step.success_rate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{fmtMs(step.p50_ms)}</TableCell>
                    <TableCell align="right">{fmtMs(step.p95_ms)}</TableCell>
                    <TableCell>
                      {step.share_of_run === null ? (
                        '–'
                      ) : (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, step.share_of_run * 100)}
                            sx={{ flex: 1, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'right' }}>
                            {fmtPct(step.share_of_run, 0)}
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={step.upgraded ? `${fmtNum(step.upgraded)} more ran the replacement for a deprecated model (not a backup)` : ''}>
                        <Typography variant="body2" color={(step.backup_share ?? 0) >= STEP_BACKUP_WARN ? 'warning.main' : undefined}>
                          {step.fallback ? `${fmtPct(step.backup_share)} (${fmtNum(step.fallback)})` : '–'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{step.model ? <Chip size="small" variant="outlined" label={step.model} /> : '–'}</TableCell>
                    <TableCell align="right">{step.runs ? fmtCredits((step.credits / step.runs) * 1000) : '–'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <SectionTitle
              title="Run time and time limit"
              help="Completed run times, the automatic time limit learned from them, and runs that timed out."
            />
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer>
                <ComposedChart data={rows} margin={{ top: 10, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
                  <YAxis yAxisId="s" tick={{ fontSize: 11 }} unit="s" />
                  <YAxis yAxisId="n" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <ChartTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="n" dataKey="timedOut" name="Timed out (runs)" fill="#e57373" barSize={8} />
                  <Line yAxisId="s" dataKey="p50" name="p50" stroke="#90caf9" dot={false} connectNulls />
                  <Line yAxisId="s" dataKey="p95" name="p95" stroke="#1976d2" dot={false} connectNulls />
                  <Line yAxisId="s" dataKey="p99" name="p99" stroke="#0d47a1" dot={false} strokeDasharray="2 2" connectNulls />
                  <Line yAxisId="s" type="stepAfter" dataKey="limit" name="Learned time limit" stroke="#f57c00" strokeWidth={2} dot={false} strokeDasharray="6 3" connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <SectionTitle title="Outcomes" help="How each run ended." />
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={rows} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <ChartTooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="clean" stackId="o" name="Completed" fill="#66bb6a" />
                      <Bar dataKey="backup" stackId="o" name="Completed, a step used a backup" fill="#ffb74d" />
                      <Bar dataKey="timeout" stackId="o" name="Timed out" fill="#e57373" />
                      <Bar dataKey="failed" stackId="o" name="Failed" fill="#b71c1c" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <SectionTitle title="Cost" help="Credits per 1,000 runs." />
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={rows} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <ChartTooltip />
                      <Line dataKey="per1k" name="Credits / 1k runs" stroke="#2e7d32" dot={false} connectNulls />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <SectionTitle title="Time limit" help="Set in the chain's Reliability settings. Time paused waiting for you does not count." />
              {canEditReliability && (
                <Button size="small" onClick={() => setReliabilityOpen(true)}>
                  Reliability settings
                </Button>
              )}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
              <Chip size="small" label={MODE_LABELS[tl.mode] || tl.mode} />
              {tl.mode === 'auto' && <Chip size="small" label={`Preset: ${tl.preset}`} />}
              <Chip size="small" color="primary" label={`Now: ${fmtS(tl.current_limit_s)}`} />
              {tl.mode !== 'auto' && <Chip size="small" variant="outlined" label={`Automatic would be ${fmtS(tl.auto_limit_s)}`} />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {tl.current_note}
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              What each preset would do
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Preset</TableCell>
                  <TableCell align="right">Limit</TableCell>
                  <TableCell align="right">Recent runs cut off</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.what_if.map(w => (
                  <TableRow key={w.preset} selected={tl.mode === 'auto' && tl.preset === w.preset}>
                    <TableCell>
                      <Tooltip title={PRESET_HELP[w.preset] || ''}>
                        <span style={{ cursor: 'help' }}>{w.preset}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{fmtS(w.limit_s)}</TableCell>
                    <TableCell align="right">
                      {w.would_cut_off === null ? '–' : `${fmtNum(w.would_cut_off)} of ${fmtNum(w.runs_checked)} (${fmtPct(w.cut_off_share)})`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary">
              Checked against up to 1,000 recent completed runs. A run that hits the limit stops with an error.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <SectionTitle title="Errors" />
            {data.errors.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No failed runs.
              </Typography>
            ) : (
              data.errors.map(e => (
                <Box key={e.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25, gap: 2 }}>
                  <Typography variant="body2">{e.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {fmtNum(e.runs)}
                  </Typography>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      {reliabilityOpen && (
        <ReliabilityDialog
          open
          onClose={() => setReliabilityOpen(false)}
          targetType="chain"
          targetId={data.chain.id}
          targetName={data.chain.name}
          onSaved={() => setReloadKey(k => k + 1)}
        />
      )}
    </Box>
  );
};

export default ChainPerformance;
